import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, TrendingUp, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube,
  Minus, Plus, X
} from 'lucide-react'
import { useProgressStore, PASS_SCORE, useUnitProgress } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG, N1_FRAGMENT_DB,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'N1-1'

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string[]
  concept: string
  misconception?: string
  misconceptionCode?: string
  learnStepId?: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'N01', number: 1, category: '소수·합성수', difficulty: 'easy',
    question: '다음 중 소수(prime number)인 것을 모두 고른 것은?',
    choices: ['1, 2, 3', '2, 3, 5', '1, 3, 7', '4, 6, 9'],
    answer: 1,
    explanation: [
      '소수: 약수가 정확히 2개(1과 자기 자신)인 2 이상의 자연수',
      '1 → 약수 1개뿐 → 소수 아님',
      '2 → 약수 {1,2} → 소수 ✓, 3 → {1,3} → 소수 ✓, 5 → {1,5} → 소수 ✓',
      '4 → {1,2,4}, 6 → {1,2,3,6}, 9 → {1,3,9} → 모두 합성수',
    ],
    concept: '소수·합성수', misconceptionCode: 'NM01', learnStepId: 'NS01',
    misconception: '1은 소수가 아닙니다! 약수가 1개뿐이라 소수 정의(약수 2개)에 맞지 않아요.',
  },
  {
    id: 'N02', number: 2, category: '소수·합성수', difficulty: 'easy',
    question: '51은 소수인가요, 합성수인가요?',
    choices: ['소수 (약수가 1과 51뿐)', '합성수 (51 = 3 × 17)', '소수도 합성수도 아님', '소수 (홀수이므로)'],
    answer: 1,
    explanation: [
      '51이 소수인지 확인: 2, 3, 5, 7로 나누어 보자',
      '51 ÷ 3 = 17 (나누어 떨어짐!)',
      '따라서 51 = 3 × 17 → 약수가 1, 3, 17, 51 → 4개',
      '약수가 3개 이상이므로 합성수',
    ],
    concept: '소수·합성수', misconceptionCode: 'NM02', learnStepId: 'NS01',
    misconception: '홀수라고 항상 소수는 아닙니다. 51처럼 3의 배수인 홀수는 합성수예요!',
  },
  {
    id: 'N03', number: 3, category: '거듭제곱', difficulty: 'easy',
    question: '2⁵의 값은?',
    choices: ['10', '25', '32', '64'],
    answer: 2,
    explanation: [
      '2⁵ = 2를 5번 곱한 것',
      '2 × 2 = 4 → 4 × 2 = 8 → 8 × 2 = 16 → 16 × 2 = 32',
    ],
    concept: '거듭제곱', misconceptionCode: 'NM03', learnStepId: 'NS02',
    misconception: '2⁵ ≠ 2×5 = 10! 지수는 곱하는 횟수입니다.',
  },
  {
    id: 'N04', number: 4, category: '거듭제곱', difficulty: 'easy',
    question: '3 × 3 × 3 × 3을 거듭제곱으로 나타내면?',
    choices: ['3 × 4', '4³', '3⁴', '12'],
    answer: 2,
    explanation: [
      '3이 4번 반복되어 곱해짐',
      '밑(base) = 3, 지수(exponent) = 4',
      '3⁴ = 3 × 3 × 3 × 3 = 81',
    ],
    concept: '거듭제곱', misconceptionCode: 'NM03', learnStepId: 'NS02',
  },
  {
    id: 'N05', number: 5, category: '거듭제곱', difficulty: 'medium',
    question: '2³ + 3² - 5¹의 값은?',
    choices: ['10', '12', '8', '6'],
    answer: 1,
    explanation: [
      '2³ = 2×2×2 = 8',
      '3² = 3×3 = 9',
      '5¹ = 5',
      '8 + 9 - 5 = 12',
    ],
    concept: '거듭제곱', learnStepId: 'NS02',
  },
  {
    id: 'N06', number: 6, category: '소인수분해', difficulty: 'easy',
    question: '36을 소인수분해한 것으로 옳은 것은?',
    choices: ['4 × 9', '2² × 3²', '2 × 18', '6²'],
    answer: 1,
    explanation: [
      '소인수분해: 소수의 곱으로만 나타내야 함',
      '4 × 9 → 4=2², 9=3² → 합성수 포함 (×)',
      '2² × 3² = 4 × 9 = 36 → 모두 소수만! ✓',
      '2 × 18 → 18은 합성수 (×)',
      '6² = 36이지만 6은 합성수 (×)',
    ],
    concept: '소인수분해', misconceptionCode: 'NM05', learnStepId: 'NS03',
    misconception: '소인수분해는 반드시 소수만의 곱으로 나타내야 합니다!',
  },
  {
    id: 'N07', number: 7, category: '소인수분해', difficulty: 'medium',
    question: '72를 소인수분해하면?',
    choices: ['2² × 3³', '2³ × 3²', '2⁴ × 3', '2 × 6²'],
    answer: 1,
    explanation: [
      '2 | 72 → 36, 2 | 36 → 18, 2 | 18 → 9',
      '3 | 9 → 3, 3 | 3 → 1',
      '72 = 2 × 2 × 2 × 3 × 3 = 2³ × 3²',
    ],
    concept: '소인수분해', learnStepId: 'NS03',
  },
  {
    id: 'N08', number: 8, category: '소인수분해', difficulty: 'medium',
    question: '120의 소인수를 모두 구하면?',
    choices: ['2, 3', '2, 5', '2, 3, 5', '2, 3, 5, 7'],
    answer: 2,
    explanation: [
      '120 = 2³ × 3 × 5',
      '소인수(소수인 약수): 2, 3, 5',
    ],
    concept: '소인수분해', learnStepId: 'NS03',
  },
  {
    id: 'N09', number: 9, category: '소인수분해', difficulty: 'hard',
    question: '180 = 2ᵃ × 3ᵇ × 5ᶜ 일 때, a + b + c의 값은?',
    choices: ['3', '4', '5', '6'],
    answer: 2,
    explanation: [
      '180 = 2² × 3² × 5¹',
      'a = 2, b = 2, c = 1',
      'a + b + c = 2 + 2 + 1 = 5',
    ],
    concept: '소인수분해', learnStepId: 'NS03',
  },
  {
    id: 'N10', number: 10, category: '최대공약수', difficulty: 'easy',
    question: 'GCD(12, 18)을 소인수분해로 구하면?',
    choices: ['2', '4', '6', '36'],
    answer: 2,
    explanation: [
      '12 = 2² × 3, 18 = 2 × 3²',
      '공통 소인수: 2¹ × 3¹',
      'GCD = 2 × 3 = 6',
    ],
    concept: '최대공약수', misconceptionCode: 'NM07', learnStepId: 'NS04',
    misconception: 'GCD에서는 지수가 작은 것을 선택합니다!',
  },
  {
    id: 'N11', number: 11, category: '최대공약수', difficulty: 'medium',
    question: 'GCD(24, 36, 60)을 구하면?',
    choices: ['4', '6', '12', '24'],
    answer: 2,
    explanation: [
      '24 = 2³ × 3, 36 = 2² × 3², 60 = 2² × 3 × 5',
      '2: min(3,2,2)=2 → 2², 3: min(1,2,1)=1 → 3¹',
      'GCD = 2² × 3 = 12',
    ],
    concept: '최대공약수', learnStepId: 'NS04',
  },
  {
    id: 'N12', number: 12, category: '최소공배수', difficulty: 'easy',
    question: 'LCM(12, 18)을 소인수분해로 구하면?',
    choices: ['6', '18', '36', '216'],
    answer: 2,
    explanation: [
      '12 = 2² × 3, 18 = 2 × 3²',
      '2: max(2,1)=2 → 2², 3: max(1,2)=2 → 3²',
      'LCM = 2² × 3² = 36',
    ],
    concept: '최소공배수', misconceptionCode: 'NM08', learnStepId: 'NS05',
    misconception: 'LCM에서는 지수가 큰 것을 선택합니다!',
  },
  {
    id: 'N13', number: 13, category: '최소공배수', difficulty: 'medium',
    question: 'LCM(8, 12, 15)를 구하면?',
    choices: ['60', '120', '240', '360'],
    answer: 1,
    explanation: [
      '8 = 2³, 12 = 2² × 3, 15 = 3 × 5',
      '2: max(3,2,0)=3, 3: max(0,1,1)=1, 5: 1',
      'LCM = 2³ × 3 × 5 = 120',
    ],
    concept: '최소공배수', learnStepId: 'NS05',
  },
  {
    id: 'N14', number: 14, category: 'GCD·LCM 관계', difficulty: 'medium',
    question: 'GCD(a,b) = 6, LCM(a,b) = 60일 때, a × b의 값은?',
    choices: ['10', '66', '360', '720'],
    answer: 2,
    explanation: [
      '공식: GCD(a,b) × LCM(a,b) = a × b',
      'a × b = 6 × 60 = 360',
    ],
    concept: 'GCD·LCM 관계', learnStepId: 'NS05',
  },
  {
    id: 'N15', number: 15, category: 'GCD 활용', difficulty: 'medium',
    question: '사탕 42개와 초콜릿 56개를 최대한 많은 학생에게 똑같이 나눠줄 때, 학생 수는?',
    choices: ['6명', '7명', '14명', '21명'],
    answer: 2,
    explanation: [
      '"최대한 많은 학생" → GCD 문제',
      '42 = 2 × 3 × 7, 56 = 2³ × 7',
      'GCD(42, 56) = 2 × 7 = 14명',
    ],
    concept: 'GCD 활용', misconceptionCode: 'NM10', learnStepId: 'NS06',
    misconception: '"나눠주는" 문제는 GCD(최대공약수)를 사용합니다!',
  },
  {
    id: 'N16', number: 16, category: 'LCM 활용', difficulty: 'medium',
    question: 'A 신호등은 3분마다, B 신호등은 5분마다 초록불로 바뀐다. 동시에 초록불이 된 후, 다시 동시에 초록불이 되는 것은 몇 분 후인가?',
    choices: ['3분', '5분', '15분', '30분'],
    answer: 2,
    explanation: [
      '"다시 동시에 만나는 시간" → LCM 문제',
      'LCM(3, 5) = 15 (서로소)',
      '15분 후에 다시 동시에 초록불이 됨',
    ],
    concept: 'LCM 활용', misconceptionCode: 'NM10', learnStepId: 'NS06',
    misconception: '"함께 만나는" 문제는 LCM(최소공배수)를 사용합니다!',
  },
  {
    id: 'N17', number: 17, category: 'GCD 활용', difficulty: 'hard',
    question: '가로 48cm, 세로 36cm인 직사각형을 빈틈없이 채울 수 있는 가장 큰 정사각형 타일의 한 변의 길이는?',
    choices: ['4cm', '6cm', '12cm', '18cm'],
    answer: 2,
    explanation: [
      '"가장 큰 정사각형" → GCD 문제',
      '48 = 2⁴ × 3, 36 = 2² × 3²',
      'GCD(48, 36) = 2² × 3 = 12cm',
    ],
    concept: 'GCD 활용', learnStepId: 'NS06',
  },
  {
    id: 'N18', number: 18, category: 'LCM 활용', difficulty: 'hard',
    question: '어떤 자연수를 6으로 나누면 2가 남고, 9로 나누면 5가 남는다. 이런 수 중 가장 작은 것은?',
    choices: ['14', '32', '68', '50'],
    answer: 0,
    explanation: [
      '구하는 수 + 4 는 6과 9의 공배수',
      'LCM(6, 9) = 18',
      '구하는 수 + 4 = 18 → 구하는 수 = 14',
      '검산: 14÷6=2…2 ✓, 14÷9=1…5 ✓',
    ],
    concept: 'LCM 활용', learnStepId: 'NS06',
  },
  {
    id: 'N19', number: 19, category: '소인수분해 종합', difficulty: 'hard',
    question: '자연수 n에 대해 n × 12가 어떤 자연수의 제곱이 되려면, n의 최솟값은?',
    choices: ['2', '3', '4', '6'],
    answer: 1,
    explanation: [
      '12 = 2² × 3',
      'n × 12 = n × 2² × 3',
      '제곱수: 모든 소인수의 지수가 짝수여야 함',
      '2²는 이미 짝수 ✓, 3¹은 홀수 → 3이 필요',
      'n의 최솟값 = 3',
    ],
    concept: '소인수분해 종합', learnStepId: 'NS03',
  },
  {
    id: 'N20', number: 20, category: '종합', difficulty: 'hard',
    question: '두 자연수의 GCD가 6이고 LCM이 180일 때, 두 수의 합으로 가능한 것은?\n(단, 두 수는 서로 다른 수이다)',
    choices: ['60', '66', '96', '120'],
    answer: 1,
    explanation: [
      'GCD = 6이므로 두 수를 6a, 6b (GCD(a,b)=1)',
      'LCM = 6ab = 180 → ab = 30',
      '서로소 쌍: (1,30),(2,15),(3,10),(5,6)',
      '(5,6): 30+36 = 66 ✓',
    ],
    concept: 'GCD·LCM 종합', learnStepId: 'NS04',
  },
]

interface Props { onGoLearn?: (stepId: string) => void }

export default function N1TestPage({ onGoLearn }: Props) {
  const [phase, setPhase] = useState<'intro' | 'test' | 'result'>('intro')
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [showResult, setShowResult] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [expandedExpl, setExpandedExpl] = useState<Set<number>>(new Set())
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  useEffect(() => {
    if (phase === 'test') {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [phase])

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  const handleSubmit = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: seconds,
      answers: answers as number[],
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('result')
  }

  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const wrongQuestions = TEST_QUESTIONS.filter((_, i) => answers[i] !== TEST_QUESTIONS[i].answer)
  const answeredCount = answers.filter(a => a !== null).length

  // ── 인트로 ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-6xl mb-4">🔢</div>
          <h1 className="text-2xl font-black text-slate-800 mb-1">소인수분해 실전 TEST</h1>
          <p className="text-slate-500 mb-6 text-sm">N1-1 단원 전 범위 · {TEST_QUESTIONS.length}문항 · 25분</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: <PenTool size={18} className="text-blue-500" />, label: '문항 수', value: `${TEST_QUESTIONS.length}문항` },
              { icon: <Clock size={18} className="text-orange-500" />, label: '권장 시간', value: '25분' },
              { icon: <Trophy size={18} className="text-yellow-500" />, label: '합격 기준', value: `${PASS_SCORE}점` },
            ].map(item => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                <div className="flex justify-center mb-1">{item.icon}</div>
                <p className="text-xs text-slate-500">{item.label}</p>
                <p className="text-sm font-black text-slate-800">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="text-left bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-xs font-bold text-blue-700 mb-2">📋 출제 범위</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
              {[
                'NS01 소수와 합성수',
                'NS02 거듭제곱과 지수',
                'NS03 소인수분해 방법',
                'NS04 최대공약수(GCD)',
                'NS05 최소공배수(LCM)',
                'NS06 GCD·LCM 활용',
              ].map(t => (
                <div key={t} className="flex items-center gap-1">
                  <span className="text-blue-400">•</span> {t}
                </div>
              ))}
            </div>
          </div>
          {unitProgress.testRecords.length > 0 && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left">
              <p className="text-xs font-bold text-amber-700 mb-1">📊 이전 응시 기록</p>
              <p className="text-sm font-black text-amber-800">
                최고 점수: {unitProgress.bestScore}점 {unitProgress.passed && '🏆 합격'}
              </p>
              <p className="text-xs text-amber-600">총 {unitProgress.testRecords.length}회 응시</p>
            </div>
          )}
          <button
            onClick={() => { setPhase('test'); setCurrent(0); setSeconds(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)) }}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-black text-lg shadow-lg hover:shadow-xl transition-all"
          >
            시험 시작 →
          </button>
        </div>
      </div>
    )
  }

  // ── 시험 ────────────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[current]
    const answered = answers[current]

    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">{current + 1} / {TEST_QUESTIONS.length}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
              q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {q.difficulty === 'easy' ? '쉬움' : q.difficulty === 'medium' ? '보통' : '어려움'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono font-bold text-sm bg-slate-100 text-slate-700">
            <Clock size={14} /> {fmt(seconds)}
          </div>
        </div>
        <div className="bg-slate-100 rounded-full h-1.5 mb-5 overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
        </div>
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-bold text-slate-800 leading-relaxed mb-5 whitespace-pre-line">
            {q.number}. {q.question}
          </p>
          <div className="space-y-2.5">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                  answered === i
                    ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-blue-300 text-slate-700'
                }`}
              >
                <span className="font-black mr-2 text-slate-400">{['①','②','③','④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={current === 0} onClick={() => setCurrent(c => c - 1)}
            className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white font-bold text-sm hover:bg-blue-600">
              다음 →
            </button>
          ) : (
            <button onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm hover:opacity-90">
              제출하기 ✓
            </button>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                i === current ? 'bg-blue-500 text-white' :
                answers[i] !== null ? 'bg-emerald-100 text-emerald-700' :
                'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-2">
          답변 완료: {answeredCount}/{TEST_QUESTIONS.length}
        </p>
      </div>
    )
  }

  // ── 결과 ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div className={`card text-center border-2 ${passed ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'}`}>
        <div className="text-5xl mb-3">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-3xl font-black mb-1" style={{ color: passed ? '#059669' : '#ea580c' }}>{score}점</h2>
        <p className={`text-base font-bold mb-4 ${passed ? 'text-emerald-700' : 'text-orange-700'}`}>
          {passed ? '🎉 합격! 소인수분해 완전 마스터!' : `아직 ${PASS_SCORE - score}점 부족해요. 다시 도전!`}
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-5">
          {[
            { label: '정답', value: `${correctCount}문항`, color: 'text-emerald-600' },
            { label: '오답', value: `${TEST_QUESTIONS.length - correctCount}문항`, color: 'text-red-500' },
            { label: '소요 시간', value: fmt(seconds), color: 'text-slate-600' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-xl p-2">
              <p className={`text-lg font-black ${item.color}`}>{item.value}</p>
              <p className="text-xs text-slate-500">{item.label}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => {
            setPhase('intro')
            setAnswers(Array(TEST_QUESTIONS.length).fill(null))
            setSeconds(0)
            setShowResult(false)
            setExpandedExpl(new Set())
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"
        >
          <RotateCcw size={14} /> 다시 풀기
        </button>
      </div>

      {/* AI 취약점 분석 */}
      {predictedFragments.length > 0 && (
        <div className="card border-2 border-violet-200 bg-violet-50">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} className="text-violet-600" />
            <span className="text-sm font-black text-violet-800">🤖 AI 취약점 분석</span>
          </div>
          <div className="space-y-2">
            {predictedFragments.map(pred => {
              const tagConf = FRAGMENT_TAG_CONFIG[pred.tag]
              return (
                <div key={pred.fragment.id} className={`rounded-xl border ${tagConf.color} p-3`}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{pred.fragment.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tagConf.badge}`}>{tagConf.label}</span>
                        <span className="text-xs font-mono text-slate-500">{pred.fragment.misconceptionCode}</span>
                      </div>
                      <p className="text-xs font-bold text-slate-800">{pred.fragment.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{pred.fragment.fixTip}</p>
                      {onGoLearn && (
                        <button onClick={() => onGoLearn(pred.fragment.stepId)}
                          className="mt-2 text-xs px-2.5 py-1 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700">
                          → {pred.fragment.stepTitle} 학습
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 오답 노트 */}
      {wrongQuestions.length > 0 && (
        <div className="card">
          <button onClick={() => setShowResult(r => !r)} className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              <span className="text-sm font-black text-slate-800">오답 노트 ({wrongQuestions.length}문제)</span>
            </div>
            {showResult ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showResult && (
            <div className="mt-4 space-y-4">
              {wrongQuestions.map(q => {
                const qIdx = TEST_QUESTIONS.indexOf(q)
                const myAns = answers[qIdx]
                const isExpanded = expandedExpl.has(qIdx)
                const video = getApprovedByTag(q.learnStepId ?? '')
                return (
                  <div key={q.id} className="border-2 border-red-100 rounded-xl overflow-hidden">
                    <div className="bg-red-50 px-4 py-3">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-black text-red-600">문제 {q.number}</span>
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{q.category}</span>
                        {q.misconceptionCode && (
                          <span className="text-xs font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
                            {q.misconceptionCode}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-800 whitespace-pre-line">{q.question}</p>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {q.choices.map((c, i) => (
                          <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${
                            i === q.answer
                              ? 'bg-emerald-100 text-emerald-800 font-bold border border-emerald-300'
                              : i === myAns
                                ? 'bg-red-100 text-red-700 border border-red-200'
                                : 'bg-slate-50 text-slate-500'
                          }`}>
                            {i === q.answer && <CheckCircle size={10} className="text-emerald-600 flex-shrink-0" />}
                            {i === myAns && i !== q.answer && <XCircle size={10} className="text-red-500 flex-shrink-0" />}
                            {['①','②','③','④'][i]} {c}
                          </div>
                        ))}
                      </div>
                      {q.misconception && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-xs font-bold text-amber-700">⚠️ 오개념 경고</p>
                          <p className="text-xs text-amber-800 mt-0.5">{q.misconception}</p>
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedExpl(s => {
                          const n = new Set(s); n.has(qIdx) ? n.delete(qIdx) : n.add(qIdx); return n
                        })}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-semibold"
                      >
                        <Lightbulb size={11} />{isExpanded ? '해설 닫기' : '해설 보기'}
                      </button>
                      {isExpanded && (
                        <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                          {q.explanation.map((line, i) => (
                            <p key={i} className="text-xs text-slate-700 leading-relaxed">{line}</p>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        {onGoLearn && q.learnStepId && (
                          <button onClick={() => onGoLearn(q.learnStepId!)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 text-white font-bold hover:bg-blue-600 flex items-center gap-1">
                            <BookOpen size={11} /> 개념 다시 보기 ({q.learnStepId})
                          </button>
                        )}
                        {video && (
                          <button onClick={() => setVideoModal({ stepId: q.learnStepId!, label: q.concept })}
                            className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 flex items-center gap-1">
                            <Youtube size={11} /> 영상
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* 전체 답안 */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 size={16} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-700">전체 답안 확인</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {TEST_QUESTIONS.map((q, i) => {
            const correct = answers[i] === q.answer
            return (
              <div key={i} className={`rounded-lg p-2 text-center ${
                correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
              }`}>
                <p className="text-xs font-bold text-slate-500">{i + 1}번</p>
                {correct
                  ? <CheckCircle size={14} className="text-emerald-500 mx-auto" />
                  : <XCircle size={14} className="text-red-500 mx-auto" />}
              </div>
            )
          })}
        </div>
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          viewerId="student_001"
          onClose={() => setVideoModal(null)}
          onCompleted={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
